import React, { useCallback, useMemo, useRef, useState } from 'react';
import { isValid, parseISO } from 'date-fns';
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { capitalizeFirstLetter, formatTZDate } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import CrewKPIList from './CrewKPIList';


const CrewKPI = ({ rows, onKpiChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const KPI_OPTIONS = useMemo(() => ["A", "B", "C", "0"], []);
  
  const formatGeneralValue = useCallback((value, indent = 0) => {
    const spacing = " ".repeat(indent * 2);
    if (value == null) return "";

    // Date        
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)) {
      const date = parseISO(value);
      if (isValid(date)) {
        if (value.endsWith("T00:00:00.000Z")) {
          return formatTZDate(date);
        }

        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const seconds = date.getUTCSeconds();
        if (hours === 0 && minutes === 0 && seconds === 0) {
          return formatTZDate(date);
        }
        return formatTZDate(date, "UTC", "HH:mm dd/MM/yyyy");
      }
    }

    // Date, Number, Boolean, String
    if (value instanceof Date) return formatTZDate(value, "UTC", "dd/MM/yyyy");
    if (typeof value === "number") return value.toLocaleString("vi-VN");
    if (typeof value === "boolean")
      return value ? (
        <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
      ) : (
        <CloseIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
      );
    if (typeof value === "string") return value;

    // Array
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      if (value.every(v => typeof v === "string")) return value.join(", ");

      // Array of Objects
      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {value.map((item, idx) => {
            if (typeof item === "object" && item !== null) {
              return (
                `${spacing}- ` +
                Object.entries(item)
                  .map(([k, v]) => `${k}: ${formatGeneralValue(v, indent + 1)}`)
                  .join(", ")
              )};
              return `${spacing}- ${formatGeneralValue(item, indent + 1)}`;
            }).join("\n")
          }
        </Typography>
      );
    }

    // Object
    if (typeof value === "object") {
      const entries = Object.entries(value);
      if (entries.length === 0) return "{}";

      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {entries
            .map(([k, v]) => `${spacing}${k}: ${formatGeneralValue(v, indent + 1)}`)
            .join("\n")}
        </Typography>
      );
    }

    return String(value);
  }, [theme]);
  
  const columns = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const keptFields = ["_id", "codes", "kpi", "description"];
    
    const allKeys = new Set();
    rows.forEach(row => {
      Object.keys(row).forEach(k => allKeys.add(k));
    });
    const dynamicFields = Array.from(allKeys).filter(k => !keptFields.includes(k));

    const hasCodes = rows.some(row => row.codes && Object.keys(row.codes).length > 0);

    return [
      {
        field: "no",
        headerName: "No",
        filterable: false,
        sortable: false,
        width: 60,
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      ...dynamicFields.map((key) => {
        const maxCellLength = Math.max(
          key.length,
          ...rows?.map(row => row[key] ? String(row[key]).length : 0)
        );

        const specialWidths = {
          crewCode: 60,
          crewName: 220,
        };
        const columnWidth = specialWidths[key] ?? Math.min(Math.max(maxCellLength * 8, 68), 72);

        const isNumber = rows.every(
          (row) => row[key] == null ||
            typeof row[key] === "number" ||
            (!isNaN(row[key]) && row[key] !== "")
        );

        return {
          field: key,
          headerName: capitalizeFirstLetter(key),
          editable: false, 
          width: columnWidth,
          align: isNumber ? "right" : "left",
          renderCell: (params) => (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {formatGeneralValue(params.value)}
            </Box>
          )
        };
      }),
      ...(hasCodes ? [{
        field: "codes",
        headerName: "Codes",
        sortable: false,
        width: 80,
        renderCell: (params) => {
          const value = params.value || {};
          const entries = Object.entries(value).sort((a, b) => b[1] - a[1]);
          if (!entries.length) return "";
          return (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              style={{ lineHeight: 1.4 }}
            >
              {entries.map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
            </Box>
          );
        },
      }] : []),
      {
        field: "kpi",
        headerName: "KPI",
        width: 68,
        align: "center",
        editable: true, 
        type: "singleSelect",
        valueOptions: KPI_OPTIONS,
        cellClassName: (params) => {
          if (!params.value) return "";
          return params.value !== "A" ? "kpi-warning" : "";
        }
      },
      {
        field: "description",
        headerName: "Description",
        width: 200,        
      }
    ];
  }, [rows, KPI_OPTIONS, formatGeneralValue]);

    
  const styles = {
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .kpi-warning": {
      backgroundColor: theme.palette.neutral.light,
      color: theme.palette.error.dark,
      fontWeight: "bold",
    },
  };


  if (isMobile) {
    return (
      <Box 
        ref={scrollRef} 
        height="100%" 
        sx={{ overflowY: 'auto' }}
      >
        <CrewKPIList
          rows={rows}
          kpiOptions={KPI_OPTIONS}
          onKpiChange={onKpiChange}
        />

        <ScrollToTop scrollRef={scrollRef} />
      </Box>
    );
  }

  return (
    <Box height="100%">
      <DataGridComponent
        rows={rows}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        props={{
          processRowUpdate: (newRow, oldRow) => {
            if (newRow.kpi !== oldRow.kpi) {
              onKpiChange(newRow._id, newRow.kpi);
            }
            return newRow;
          },
          onProcessRowUpdateError: (error) => console.error(error),
        }}
        sx={styles}
      />
    </Box>
  );
};

export default CrewKPI;
